import type { OnDestroy, TemplateRef } from '@angular/core';
import { Component } from '@angular/core';
import type {
  MatLegacyDialogConfig as MatDialogConfig,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import type { ComponentType } from '@angular/cdk/portal';
import { getBookByAlias } from 'src/app/data/books/utils';
import type { TBookAlias, TProductAlias } from '@shared/jp/product';
import { tmBookAliasArr } from 'src/app/data/books';
import { firstValueFrom, map, shareReplay, Subject, takeUntil } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { ROUTE_PATH_PARAM_GIFT_ID } from '@studiobuki/web-core/lib/routing';
import { getGiftAliasArr, getGiftBookByAlias } from '@shared/common/gift';
import { BookFormDialogComponent } from './dialogs/book-form-dialog/book-form-dialog.component';
import type { IGiftLoadData } from './components/section-gift-use/interfaces';
import { BookSelectDialogComponent } from './dialogs/book-select-dialog/book-select-dialog.component';

@Component({
  templateUrl: './gift-use-page.component.html',
  styleUrls: ['./gift-use-page.component.scss'],
})
export class GiftUsePageComponent implements OnDestroy {
  private readonly _destroy$ = new Subject<void>();

  readonly giftId$ = this._route.paramMap.pipe(
    map((paramMap) => paramMap.get(ROUTE_PATH_PARAM_GIFT_ID)),
    // filterNullable(),
    shareReplay(1),
    takeUntil(this._destroy$),
  );

  // readonly alias$ = this._route.paramMap.pipe(
  //   map((paramMap) => paramMap.get(ROUTE_PATH_PARAM_ALIAS)),
  //   // filterNullable(),
  //   shareReplay(1),
  //   takeUntil(this._destroy$),
  // );

  constructor(
    private _dialog: MatDialog,
    private _route: ActivatedRoute,
    private _router: Router,
  ) {}

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  /**
   * Opens a modal dialog containing the given component.
   * @param componentOrTemplateRef Type of the component to load into the dialog,
   *     or a TemplateRef to instantiate as the dialog content.
   * @param config Extra configuration options.
   * @returns Reference to the newly-opened dialog.
   */

  private _showMatDialog<
    T extends {
      data: any;
      dialogRef: MatDialogRef<T>;
    },
    D = T['data'],
    R = Parameters<T['dialogRef']['close']>[0],
  >(
    componentOrTemplateRef: ComponentType<T> | TemplateRef<T>,
    config?: MatDialogConfig<D>,
  ): MatDialogRef<T, R> {
    return this._dialog.open<T, D, R>(componentOrTemplateRef, {
      panelClass: 'simple-overlay-pane',
      disableClose: true,
      ...config,
    });
  }

  // public async onGiftIdChange

  public async onGiftLoad(gift: IGiftLoadData) {
    // const giftId = await firstValueFrom(this.giftId$);

    // if (giftId !== gift.docId) {
    //   await this._router.navigate([ROUTE_PATH_GIFT_USE_NO_LOGO, gift.docId]);
    // }

    const aliasArr = getGiftAliasArr(gift.book, tmBookAliasArr);
    let selectedAlias: TProductAlias | undefined;

    // * show dialog if we have aliases to select from
    if (aliasArr.length > 1) {
      const ref = this._showMatDialog(BookSelectDialogComponent, {
        data: { aliasArr },
        width: '100%',
      });

      selectedAlias = await firstValueFrom(ref.afterClosed());
    } else {
      [selectedAlias] = aliasArr;
    }

    if (!selectedAlias)
      throw new Error('selected alias is required to proceed');

    const giftBook = getGiftBookByAlias(gift.book, selectedAlias);

    if (!giftBook) throw new Error('selected book is not found by alias');

    const { alias, cover } = giftBook;

    const book = getBookByAlias(alias as TBookAlias);

    const panelClass = ['simple-overlay-pane'];

    // if (alias === 'space') {
    //   panelClass.push('simple-overlay-pane_space');
    // }

    this._showMatDialog(BookFormDialogComponent, {
      data: {
        alias,
        cover,
        inputs: book.inputs,
        code: gift.docId,
      },
      panelClass,
    });
  }
}
